// Audio Engine - Tone.js wrapper for audio playback with effects
import * as Tone from 'tone';
import type { AudioClip } from '../api/types';

export interface AudioEffects {
  pitch: number;      // Semitones (-12 to +12)
  reverb: number;     // Wet value (0 to 1)
  speed: number;      // Playback rate (0.5 to 2)
  volume: number;     // dB (-60 to 0)
}

export const defaultEffects: AudioEffects = {
  pitch: 0,
  reverb: 0,
  speed: 1,
  volume: 0,
};

class AudioEngine {
  private player: Tone.Player | null = null;
  private pitchShift: Tone.PitchShift | null = null;
  private reverb: Tone.Reverb | null = null;
  private isInitialized = false;
  private currentClipId: number | null = null;
  private onPlaybackEndCallback: (() => void) | null = null;
  private onProgressCallback: ((progress: number) => void) | null = null;
  private progressInterval: number | null = null;

  /**
   * Initialize audio context (must be called after user interaction)
   */
  async init(): Promise<void> {
    if (this.isInitialized) return;
    
    await Tone.start();
    this.isInitialized = true;
    console.log('Audio engine initialized');
  }

  /**
   * Load and play an audio clip
   */
  async play(clip: AudioClip, streamUrl: string, effects: AudioEffects): Promise<void> {
    await this.init();

    // Stop current playback
    this.stop();

    // Create new player and effects chain
    this.player = new Tone.Player({
      url: streamUrl,
      autostart: false,
      onload: () => {
        console.log(`Loaded: ${clip.title}`);
      },
      onstop: () => {
        this.handlePlaybackEnd();
      },
    });

    this.pitchShift = new Tone.PitchShift({
      pitch: effects.pitch,
    });

    this.reverb = new Tone.Reverb({
      decay: 2,
      wet: effects.reverb,
    });

    // Set volume and playback rate
    Tone.getDestination().volume.value = effects.volume;
    this.player.playbackRate = effects.speed;

    // Connect effects chain
    this.player.chain(this.pitchShift, this.reverb, Tone.getDestination());

    // Wait for player to load
    await Tone.loaded();

    // Start playback
    this.player.start();
    this.currentClipId = clip.id;

    // Start progress tracking
    this.startProgressTracking();
  }

  /**
   * Pause current playback
   */
  pause(): void {
    if (this.player && this.player.state === 'started') {
      this.player.stop();
    }
  }

  /**
   * Resume playback (reload and play from current position not supported, plays from start)
   */
  resume(): void {
    if (this.player && this.player.loaded) {
      this.player.start();
      this.startProgressTracking();
    }
  }

  /**
   * Stop playback and cleanup
   */
  stop(): void {
    this.stopProgressTracking();
    
    if (this.player) {
      this.player.stop();
      this.player.dispose();
      this.player = null;
    }
    if (this.pitchShift) {
      this.pitchShift.dispose();
      this.pitchShift = null;
    }
    if (this.reverb) {
      this.reverb.dispose();
      this.reverb = null;
    }
    this.currentClipId = null;
  }

  /**
   * Stop all audio immediately (panic stop)
   */
  panicStop(): void {
    Tone.getTransport().stop();
    Tone.getTransport().cancel();
    this.stop();
  }

  /**
   * Update pitch shift effect
   */
  setPitch(semitones: number): void {
    if (this.pitchShift) {
      this.pitchShift.pitch = semitones;
    }
  }

  /**
   * Update reverb wet value
   */
  setReverb(wet: number): void {
    if (this.reverb) {
      this.reverb.wet.value = wet;
    }
  }

  /**
   * Update playback speed
   */
  setSpeed(rate: number): void {
    if (this.player) {
      this.player.playbackRate = rate;
    }
  }

  /**
   * Update master volume
   */
  setVolume(db: number): void {
    Tone.getDestination().volume.value = db;
  }

  /**
   * Get current playback state
   */
  isPlaying(): boolean {
    return this.player?.state === 'started';
  }

  /**
   * Get current clip ID
   */
  getCurrentClipId(): number | null {
    return this.currentClipId;
  }

  /**
   * Get current playback progress (0-1)
   */
  getProgress(): number {
    if (!this.player || !this.player.loaded) return 0;
    const duration = this.player.buffer.duration;
    if (duration === 0) return 0;
    // Tone.js doesn't expose current position easily, estimate based on time
    return 0;
  }

  /**
   * Set callback for playback end
   */
  onPlaybackEnd(callback: () => void): void {
    this.onPlaybackEndCallback = callback;
  }

  /**
   * Set callback for progress updates
   */
  onProgress(callback: (progress: number) => void): void {
    this.onProgressCallback = callback;
  }

  private handlePlaybackEnd(): void {
    this.stopProgressTracking();
    if (this.onPlaybackEndCallback) {
      this.onPlaybackEndCallback();
    }
  }

  private startProgressTracking(): void {
    this.stopProgressTracking();
    let startTime = Tone.now();
    
    this.progressInterval = window.setInterval(() => {
      if (this.player && this.player.loaded && this.player.state === 'started') {
        const elapsed = Tone.now() - startTime;
        const duration = this.player.buffer.duration / this.player.playbackRate;
        const progress = Math.min(elapsed / duration, 1);
        
        if (this.onProgressCallback) {
          this.onProgressCallback(progress);
        }
        
        if (progress >= 1) {
          this.handlePlaybackEnd();
        }
      }
    }, 100);
  }

  private stopProgressTracking(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }
}

// Export singleton instance
export const audioEngine = new AudioEngine();
export default audioEngine;
